import { IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';

import {
  schemaFaqJSONLD,
  schemaOrgJSONLD,
  schemaWebPageJSONLD,
  schemaBreadcrumbAndPostJSONLD,
} from '../utils/consts/jsonLdSchema';
import initializeDatadog from '../utils/datadog';
import config from '../utils/siteConfig';

interface PostNode {
  title: string;
  slug: string;
  description: {
    internal: {
      content: string;
    };
  } | null;
  body: {
    childMarkdownRemark: {
      excerpt: string;
    };
  };
  subcategories: Array<{
    title: string;
    slug: string;
  }>;
  heroImage: {
    gatsbyImageData: IGatsbyImageData;
  };
  publishDateISO: string;
  canonicalUrl?: string;
}

interface HeadProps {
  postNode: PostNode;
  pagePath: string;
  postSEO?: boolean;
  pageSEO?: boolean;
  customTitle?: boolean;
}

interface SEOProps {
  postNode?: PostNode;
  pageSEO?: boolean;
  postSEO?: boolean;
  pageUrl?: string;
  title?: string;
  faqSchema?: Array<{
    question: string;
    answer: string;
  }>;
}

/** Datadog initialization */
initializeDatadog();

const constructPageUrl = (postNode: PostNode, pagePath: string): string =>
  `${config.siteUrl}/blog/${
    postNode.subcategories[0]?.slug || ''
  }/${pagePath}/`;

export const Head: React.FC<HeadProps> = ({
  postNode,
  pagePath,
  postSEO,
  pageSEO,
  customTitle,
}) => {
  let title = config.siteTitle;
  let description = config.siteDescription;
  let image = config.shareImage ? config.siteUrl + config.shareImage : '';
  let imgWidth = config.shareImageWidth;
  let imgHeight = config.shareImageHeight;
  let pageUrl = `${config.siteUrl}/blog`;

  if (customTitle || postSEO || pageSEO) {
    title = `${postNode.title} - ${config.siteTitle}`;
    pageUrl = constructPageUrl(postNode, pagePath);
  }

  if (postSEO || pageSEO) {
    description =
      postNode.description?.internal.content ||
      postNode.body.childMarkdownRemark.excerpt;
  }

  if (postSEO && postNode.heroImage) {
    image = postNode.heroImage.gatsbyImageData.images.fallback?.src || '';
    imgWidth = postNode.heroImage.gatsbyImageData.width;
    imgHeight = postNode.heroImage.gatsbyImageData.height;
  }

  const canonicalUrl = postNode.canonicalUrl
    ? constructPageUrl(postNode, postNode.canonicalUrl)
    : undefined;

  return (
    <>
      <html lang="en" />
      {/* Meta Tags */}
      <meta name="image" content={image} />
      <meta name="description" content={description} />
      <meta name="keywords" content={config.keywords} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* OpenGraph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:type" content={postSEO ? 'article' : 'website'} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={imgWidth.toString()} />
      <meta property="og:image:height" content={imgHeight.toString()} />
      <meta property="og:description" content={description} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={config.userTwitter || ''} />
      <meta name="twitter:title" content={title} />
      <meta property="og:image" content={image} />
      <meta name="twitter:description" content={config.siteDescription} />
    </>
  );
};

const SEO: React.FC<SEOProps> = ({
  postNode,
  pageSEO,
  postSEO,
  pageUrl,
  title,
  faqSchema,
}) => (
  <>
    <script type="application/ld+json">
      {JSON.stringify(schemaOrgJSONLD)}
    </script>

    {pageSEO && pageUrl && title && (
      <script type="application/ld+json">
        {JSON.stringify(schemaWebPageJSONLD(pageUrl, title))}
      </script>
    )}

    {faqSchema && (
      <script type="application/ld+json">
        {JSON.stringify(schemaFaqJSONLD(faqSchema))}
      </script>
    )}

    {postSEO && postNode && (
      <script type="application/ld+json">
        {JSON.stringify(schemaBreadcrumbAndPostJSONLD(postNode))}
      </script>
    )}
  </>
);

export default SEO;
